import React, { createContext, useContext, useEffect, useState } from 'react';
import type { Session, User as SupabaseAuthUser } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

export interface User {
  id: string;
  email: string;
  name: string;
  plan: 'free' | 'basic' | 'premium' | 'revelation';
}

export interface AuthResult {
  success: boolean;
  status?: 'authenticated' | 'check_email' | 'oauth_redirect';
  errorCode?: string;
  errorMessage?: string;
}

export interface InsightUsage {
  date: string;
  askCount: number;
  quickCount: {
    prompt: number;
    reframe: number;
    action: number;
    metaphor: number;
  };
  deepDiveUsed: boolean;
  freeWelcomePackClaimed: boolean;
  freeWelcomePackRemaining: {
    deepDive: number;
    ask: number;
    quick: {
      prompt: number;
      reframe: number;
      action: number;
      metaphor: number;
    };
  };
}

interface AuthContextType {
  user: User | null;
  isLoggedIn: boolean;
  isGuestMode: boolean;
  isAuthLoading: boolean;
  login: (email: string, password: string) => Promise<AuthResult>;
  signup: (name: string, email: string, password: string) => Promise<AuthResult>;
  signInWithGoogle: (redirectPath?: string) => Promise<AuthResult>;
  logout: () => Promise<void>;
  setGuestMode: (enabled: boolean) => void;
  getInsightCredits: () => { ask: number; quick: number; deepDive: boolean };
  consumeInsightCredit: (type: 'ask' | 'quick' | 'deepDive') => boolean;
  getInsightUsage: () => InsightUsage;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const getInitialInsightUsage = (): InsightUsage => ({
  date: new Date().toISOString().split('T')[0],
  askCount: 0,
  quickCount: { prompt: 0, reframe: 0, action: 0, metaphor: 0 },
  deepDiveUsed: false,
  freeWelcomePackClaimed: false,
  freeWelcomePackRemaining: {
    deepDive: 1,
    ask: 5,
    quick: { prompt: 1, reframe: 1, action: 1, metaphor: 1 },
  },
});

const normalizeInsightUsage = (value: unknown): InsightUsage => {
  const initial = getInitialInsightUsage();

  if (!value || typeof value !== 'object') {
    return initial;
  }

  const parsed = value as Partial<InsightUsage>;

  return {
    date: parsed.date || initial.date,
    askCount: parsed.askCount ?? 0,
    quickCount: {
      prompt: parsed.quickCount?.prompt ?? 0,
      reframe: parsed.quickCount?.reframe ?? 0,
      action: parsed.quickCount?.action ?? 0,
      metaphor: parsed.quickCount?.metaphor ?? 0,
    },
    deepDiveUsed: parsed.deepDiveUsed ?? false,
    freeWelcomePackClaimed: parsed.freeWelcomePackClaimed ?? false,
    freeWelcomePackRemaining: {
      deepDive: parsed.freeWelcomePackRemaining?.deepDive ?? initial.freeWelcomePackRemaining.deepDive,
      ask: parsed.freeWelcomePackRemaining?.ask ?? initial.freeWelcomePackRemaining.ask,
      quick: {
        prompt: parsed.freeWelcomePackRemaining?.quick?.prompt ?? initial.freeWelcomePackRemaining.quick.prompt,
        reframe: parsed.freeWelcomePackRemaining?.quick?.reframe ?? initial.freeWelcomePackRemaining.quick.reframe,
        action: parsed.freeWelcomePackRemaining?.quick?.action ?? initial.freeWelcomePackRemaining.quick.action,
        metaphor: parsed.freeWelcomePackRemaining?.quick?.metaphor ?? initial.freeWelcomePackRemaining.quick.metaphor,
      },
    },
  };
};

const buildFallbackUser = (authUser: SupabaseAuthUser): User => ({
  id: authUser.id,
  email: authUser.email || '',
  name: authUser.user_metadata?.full_name || authUser.user_metadata?.name || authUser.email?.split('@')[0] || 'User',
  plan: 'free',
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [insightUsage, setInsightUsage] = useState<InsightUsage>(() => {
    const saved = localStorage.getItem('insightUsage');

    if (!saved) {
      return getInitialInsightUsage();
    }

    try {
      return normalizeInsightUsage(JSON.parse(saved));
    } catch {
      return getInitialInsightUsage();
    }
  });

  const isLoggedIn = !!user;

  useEffect(() => {
    if (!localStorage.getItem('insightUsage')) {
      setInsightUsage(getInitialInsightUsage());
    }
  }, []);

  const loadUserProfile = async (authUser: SupabaseAuthUser): Promise<User> => {
    const fallbackUser = buildFallbackUser(authUser);

    try {
      const { data: profile, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', authUser.id)
        .maybeSingle();

      if (error) {
        console.error('Error loading user profile:', error);
      }

      if (profile) {
        const resolvedUser: User = {
          id: authUser.id,
          email: profile.email || authUser.email || '',
          name: profile.name || fallbackUser.name,
          plan: profile.plan as User['plan'],
        };

        setUser(resolvedUser);
        return resolvedUser;
      }

      const { data: createdProfile, error: createError } = await supabase
        .from('user_profiles')
        .upsert(
          {
            user_id: authUser.id,
            email: authUser.email || null,
            name: fallbackUser.name,
            plan: 'free',
          },
          { onConflict: 'user_id' }
        )
        .select('*')
        .maybeSingle();

      if (createError) {
        console.error('Error creating missing user profile:', createError);
        setUser(fallbackUser);
        return fallbackUser;
      }

      const resolvedUser: User = {
        id: authUser.id,
        email: createdProfile?.email || fallbackUser.email,
        name: createdProfile?.name || fallbackUser.name,
        plan: (createdProfile?.plan as User['plan']) || 'free',
      };

      setUser(resolvedUser);
      return resolvedUser;
    } catch (error) {
      console.error('Exception loading user profile:', error);
      setUser(fallbackUser);
      return fallbackUser;
    }
  };

  useEffect(() => {
    let isMounted = true;

    const syncAuthState = async (session: Session | null) => {
      if (!isMounted) return;

      if (!session?.user) {
        setUser(null);
        setIsGuestMode(false);
        setIsAuthLoading(false);
        return;
      }

      await loadUserProfile(session.user);

      if (isMounted) {
        setIsGuestMode(false);
        setIsAuthLoading(false);
      }
    };

    const initAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) {
          console.error('Error initializing auth:', error);
        }

        await syncAuthState(session);
      } catch (error) {
        console.error('Error initializing auth:', error);
        if (isMounted) {
          setUser(null);
          setIsAuthLoading(false);
        }
      }
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!isMounted) return;

      if (event === 'SIGNED_OUT') {
        setUser(null);
        setIsGuestMode(false);
        setIsAuthLoading(false);
        return;
      }

      if (session?.user && ['INITIAL_SESSION', 'SIGNED_IN', 'TOKEN_REFRESHED', 'USER_UPDATED'].includes(event)) {
        void loadUserProfile(session.user).finally(() => {
          if (isMounted) {
            setIsGuestMode(false);
            setIsAuthLoading(false);
          }
        });
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('insightUsage', JSON.stringify(insightUsage));
  }, [insightUsage]);

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];

    if (insightUsage.date !== today) {
      setInsightUsage((prev) => ({
        ...prev,
        date: today,
        askCount: 0,
        quickCount: { prompt: 0, reframe: 0, action: 0, metaphor: 0 },
        deepDiveUsed: false,
      }));
    }
  }, [insightUsage]);

  const login = async (email: string, password: string): Promise<AuthResult> => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('Login error:', error);

        if (error.code === 'email_not_confirmed') {
          return {
            success: false,
            errorCode: error.code,
            errorMessage: 'Please check your email and confirm your account before signing in.',
          };
        }

        if (error.code === 'invalid_credentials') {
          return {
            success: false,
            errorCode: error.code,
            errorMessage: 'Invalid email or password.',
          };
        }

        return {
          success: false,
          errorCode: error.code,
          errorMessage: error.message || 'Login failed. Please try again.',
        };
      }

      if (data.user) {
        await loadUserProfile(data.user);
        setIsGuestMode(false);
        return { success: true, status: 'authenticated' };
      }

      return { success: false, errorMessage: 'Login failed. Please try again.' };
    } catch (error) {
      console.error('Login exception:', error);
      return { success: false, errorMessage: 'Login failed. Please try again.' };
    }
  };

  const signup = async (name: string, email: string, password: string): Promise<AuthResult> => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: {
            name,
            full_name: name,
          },
        },
      });

      if (error) {
        console.error('Signup error:', error);

        if (error.message.includes('already registered')) {
          throw new Error('Email already registered. Please sign in instead.');
        }

        if (error.message.toLowerCase().includes('rate limit')) {
          throw new Error('Too many sign-up emails were sent recently. Please wait a few minutes and try again.');
        }

        throw new Error(error.message || 'Signup failed. Please try again.');
      }

      if (data.session?.user) {
        await loadUserProfile(data.session.user);
        setIsGuestMode(false);
        return { success: true, status: 'authenticated' };
      }

      if (data.user) {
        await loadUserProfile(data.user);
        setIsGuestMode(false);
        return { success: true, status: 'check_email' };
      }

      return { success: false, errorMessage: 'Signup failed. Please try again.' };
    } catch (error) {
      console.error('Signup exception:', error);
      throw error;
    }
  };

  const signInWithGoogle = async (redirectPath = '/app/dashboard'): Promise<AuthResult> => {
    try {
      sessionStorage.setItem('postAuthRedirect', redirectPath);

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        console.error('Google OAuth error:', error);
        return {
          success: false,
          errorCode: error.code,
          errorMessage: error.message.includes('provider is not enabled')
            ? 'Google sign-in is not enabled in Supabase yet.'
            : error.message || 'Google sign-in failed. Please try again.',
        };
      }

      if (data.url) {
        return { success: true, status: 'oauth_redirect' };
      }

      return { success: false, errorMessage: 'Google sign-in could not start.' };
    } catch (error) {
      console.error('Google OAuth exception:', error);
      return { success: false, errorMessage: 'Google sign-in failed. Please try again.' };
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();

      localStorage.removeItem('mindshiftJournalEntries');
      localStorage.removeItem('mindshiftMindshifts');
      localStorage.removeItem('mindshiftActions');
      localStorage.removeItem('mindshiftNotes');
      localStorage.removeItem('mindshiftDailyMoods');
      localStorage.removeItem('mindshiftLastLogin');
      localStorage.removeItem('guestAIUsage');
      localStorage.removeItem('insightUsage');
      localStorage.removeItem('appReturnBannerDismissed');
      localStorage.removeItem('VITE_FREESOUND_API_KEY');

      setUser(null);
      setIsGuestMode(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const setGuestMode = (enabled: boolean) => {
    setIsGuestMode(enabled);

    if (enabled) {
      setInsightUsage((prev) => ({
        ...prev,
        askCount: 0,
        quickCount: { prompt: 0, reframe: 0, action: 0, metaphor: 0 },
        deepDiveUsed: false,
        freeWelcomePackClaimed: true,
        freeWelcomePackRemaining: {
          deepDive: 0,
          ask: 0,
          quick: { prompt: 0, reframe: 0, action: 0, metaphor: 0 },
        },
      }));
    }
  };

  const getInsightCredits = () => {
    if (!isLoggedIn || isGuestMode) {
      return { ask: 0, quick: { prompt: 0, reframe: 0, action: 0, metaphor: 0 }, deepDive: false };
    }

    if (user?.plan === 'revelation') {
      return {
        ask: 25,
        quick: { prompt: 10, reframe: 10, action: 10, metaphor: 10 },
        deepDive: true,
      };
    }

    if (user?.plan === 'premium') {
      return {
        ask: 10,
        quick: { prompt: 5, reframe: 5, action: 5, metaphor: 5 },
        deepDive: true,
      };
    }

    return {
      ask: 5,
      quick: { prompt: 1, reframe: 1, action: 1, metaphor: 1 },
      deepDive: true,
    };
  };

  const consumeInsightCredit = (_type: 'ask' | 'quick' | 'deepDive'): boolean => {
    return true;
  };

  const getInsightUsage = () => insightUsage;

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoggedIn,
        isGuestMode,
        isAuthLoading,
        login,
        signup,
        signInWithGoogle,
        logout,
        setGuestMode,
        getInsightCredits,
        consumeInsightCredit,
        getInsightUsage,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
